import React, { FC, useMemo } from 'react';
import { Icon, IconButton } from '@kontentino/ui';
import { usePostCommunicationContext } from 'app/modules/posts/modules/post/components/postCommunication/PostCommunicationProvider';
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose';
import CommentsHyperText from './commentsList/CommentsHyperText';
import { useTranslation } from 'react-i18next';
import { useWindowSize } from 'utils/hooks/useWindowsSize';
import { Comment } from 'app/modules/comments/types';
const LARGE_DESKTOP_VIEWPORT = 1536;
const MEDIUM_DESKTOP_VIEWPORT = 1280;
const SMALL_DESKTOP_VIEWPORT = 1024;

const getReplyMaxLength = (windowWidth: number | null) => {
  if (!windowWidth) return 25;

  if (windowWidth >= LARGE_DESKTOP_VIEWPORT) {
    return 85;
  }

  if (windowWidth >= MEDIUM_DESKTOP_VIEWPORT) {
    return 60;
  }

  if (windowWidth >= SMALL_DESKTOP_VIEWPORT) {
    return 45;
  }

  return 25;
};

type Props = {
  colorVariant: 'primary' | 'purple';
  comment: Comment;
};

const CommentsTextEditorReplyHeader: FC<Props> = ({
  comment,
  colorVariant,
}) => {
  const { t } = useTranslation();
  const { resetCommentAction } = usePostCommunicationContext();
  const { width: windowWidth } = useWindowSize();
  const isReplyingToSelf = comment?.user.its_me;

  const replyMaxLength = useMemo(
    () => getReplyMaxLength(windowWidth),
    [windowWidth],
  );

  return (
    <div className="tw-flex tw-justify-between tw-border-t tw-border-grayscale-20 tw-pt-2 ">
      <div className="tw-flex tw-w-11/12 tw-flex-col tw-gap-y-1 tw-text-sm">
        <div className="tw-font-semibold tw-text-grayscale-190">
          {t('replyingTo')} {isReplyingToSelf && t('yourself')}
          {!isReplyingToSelf && comment.user.name}
        </div>
        <div>
          <CommentsHyperText
            className={
              'tw-text-grayscale-180  [&>*>a]:!tw-text-grayscale-180 [&>a]:!tw-text-grayscale-180'
            }
            isCurrentUser={comment.user.its_me}
            disableMentionStyle
            colorVariant={colorVariant}
            mentionedUsers={comment.mentioned_users ?? []}
            options={{
              ellipsis: false,
              maxLength: replyMaxLength,
              expandable: true,
              expandableTrigger: {
                more: (
                  <span className="tw-cursor-pointer tw-lowercase tw-text-grayscale-50">
                    {`...${t('showMore')}`}
                  </span>
                ),
                less: (
                  <span className="tw-cursor-pointer tw-lowercase tw-text-grayscale-50">
                    {`...${t('showLess')}`}
                  </span>
                ),
              },
            }}
          >
            {comment.content}
          </CommentsHyperText>
        </div>
      </div>
      <IconButton
        onClick={() => resetCommentAction()}
        className="tw-h-5 tw-w-5"
        variant={'ghost'}
        icon={
          <Icon icon={faClose} className="tw-text-grayscale-100" size={16} />
        }
      />
    </div>
  );
};

export default CommentsTextEditorReplyHeader;
